import { InputGroup, type InputProps } from '@chakra-ui/react'
import { Flex, Icon, Input } from '@chakra-ui/react'
import { Search } from 'lucide-react'
import React, {
  ForwardedRef,
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { MangoCloseButton } from '~/components/shared/MangoCloseButton'
import { useSearchInputString } from '~/pages/WebPhone/atoms/SearchInput.atom'
import { SearchValue } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { tryGetValueOrDefault } from '~/utils/common'

import { textStyleKeys, textStyles } from '@mangovoice/design-system'

type SearchInputProps = Omit<InputProps, 'onChange' | 'onSubmit' | 'value'> & {
  placeholder?: string
  /** Accessible name for the input; placeholders alone don't name it. */
  inputLabel?: string
  value?: string
  shareKey?: string
  onClear?: () => void
  onChange?: (searchValue: SearchValue) => void
  onSubmit?: () => void
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (props: SearchInputProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      placeholder,
      inputLabel,
      value,
      shareKey,
      onChange,
      onClear,
      onSubmit,
      ...rest
    } = props
    const { t } = useTranslation()
    const { searchValue, setInputString, getFormattedInput } =
      useSearchInputString({ shareKey })
    const id = useId()
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const handleInputChangedInternal = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      //if externally controlled, don't set the internal input value, just hand back the formatting change
      if (value !== undefined) {
        const { clean, formatted: inputString } = getFormattedInput(
          e.target.value,
        )
        onChange?.(SearchValue.fromValueAndLabel(clean, inputString))
        return
      }

      const searchValue = setInputString(e.target.value)
      onChange?.(searchValue)
    }

    const handleClearedInternal = () => {
      setInputString('')
      onChange?.(SearchValue.empty)
      onClear?.()
      inputRef.current?.focus()
    }

    const resolvedInputValue = tryGetValueOrDefault(value, searchValue.label)
    const inputEmpty = resolvedInputValue === ''

    // Exclude direction prop to avoid type conflict with Flex
    const { direction: _direction, ...flexProps } = rest

    return (
      <Flex {...flexProps} alignSelf="stretch" display="flex" flex={1}>
        <InputGroup
          startElement={<Icon as={Search} color="fg.subtle" />}
          endElement={
            inputEmpty ? null : (
              <MangoCloseButton
                size={Size.md}
                aria-label={t('global.clearSearch', 'Clear search')}
                onClick={handleClearedInternal}
              />
            )
          }
        >
          <Input
            id={id}
            ref={inputRef}
            placeholder={placeholder}
            aria-label={inputLabel}
            value={resolvedInputValue}
            onChange={handleInputChangedInternal}
            onSubmit={onSubmit}
            variant="subtle"
            borderColor="border.default"
            borderRadius="sm"
            {...textStyles[textStyleKeys.Body2]}
          />
        </InputGroup>
      </Flex>
    )
  },
)
